<template>
  <div class="form-check" :class="{disabled: disabled}">
    <label :for="cbId" class="form-check-label">
      <input :id="cbId"
             class="form-check-input"
             type="checkbox"
             :disabled="disabled"
             :checked="isChecked"
             :aria-invalid="error ? 'true' : undefined"
             :aria-describedby="error ? errorId : undefined"
             @change="onChange" />
      <span class="form-check-sign"></span>
    </label>
    <span>
      <slot></slot>
    </span>
    <span v-if="error" :id="errorId" class="form-error" role="alert">{{ error }}</span>
  </div>
</template>

<script>
import { ref, computed, watch, useId } from 'vue'

export default {
  name: 'BaseCheckbox',
  props: {
    modelValue: {
      type: [Array, Boolean],
      default: false
    },
    // backwards compat: `checked` still accepted, maps to modelValue
    checked: {
      type: [Array, Boolean],
      default: undefined
    },
    disabled: {
      type: [Boolean, String],
      default: false
    },
    inline: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: ""
    },
    id: {
      type: String,
      default: ""
    }
  },
  emits: ['update:modelValue', 'update:checked'],
  setup(props, { emit }) {
    const autoId = `cb-${useId()}`
    const cbId = ref(props.id || autoId)
    watch(() => props.id, (v) => { if (v) cbId.value = v })
    const errorId = computed(() => `${cbId.value}-error`)
    const sourceValue = computed(() => props.checked !== undefined ? props.checked : props.modelValue)
    const model = ref(sourceValue.value)

    watch(sourceValue, (v) => { model.value = v })

    const isChecked = computed(() => {
      if (Array.isArray(model.value)) return false
      return !!model.value
    })

    const onChange = (e) => {
      const checked = e.target.checked
      model.value = checked
      emit('update:modelValue', checked)
      emit('update:checked', checked)
    }

    const inlineClass = () => {
      if (props.inline) {
        return `checkbox-inline`
      }
    }

    return {
      cbId,
      errorId,
      model,
      isChecked,
      onChange,
      inlineClass
    }
  }
}
</script>

<style scoped>
.form-check {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 10px;
  padding: 0;
}
.form-check-label {
  display: inline-flex;
  align-items: center;
  margin: 0;
  cursor: pointer;
  position: relative;
}
.form-check-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}
.form-check-sign {
  width: 19px;
  height: 19px;
  border: 1.5px solid var(--adm-border-strong);
  border-radius: 6px;
  background: var(--adm-card);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}
.form-check-input:checked + .form-check-sign {
  background: var(--adm-accent);
  border-color: var(--adm-accent);
}
.form-check-input:checked + .form-check-sign::after {
  content: '';
  width: 5px;
  height: 9px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg) translate(-1px, -1px);
}
.dark-mode .form-check-input:checked + .form-check-sign::after { border-color: #0f1420; }
.form-check-input:focus + .form-check-sign {
  border-color: var(--adm-accent);
  box-shadow: 0 0 0 3px var(--adm-accent-soft);
}
.form-check > span:last-child {
  color: var(--adm-text);
  font-size: 0.88rem;
}
.form-check.disabled { opacity: 0.55; }
.form-check.disabled .form-check-label { cursor: not-allowed; }
.form-error {
  font-size: 0.78rem;
  color: var(--adm-danger);
  margin-left: 4px;
}
</style>
